import { FC, MouseEvent, useEffect, useState } from 'react';
import {
  useAtomValue,
  useSetAtom,
  type Atom,
  type PrimitiveAtom,
  type WritableAtom,
} from 'jotai';
import {
  Button,
  DataTable,
  InlineLoading,
  InlineNotification,
  Link,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Tile,
} from '@carbon/react';
import { Add, Edit, TrashCan, Undo } from '@carbon/react/icons';
import { StatusTag, type PartyStatus } from 'components/status-tag';
import type { SectionMutationAtom } from 'lib/atoms/section-mutation';
import type {
  WellLifecycleResponse,
  WellOverviewSummary,
} from 'pages/afe-catalog/structuring/types';
// Pure status pill — no doctype coupling; shared afe-catalog copy.
import { WellWorkflowStatusBadge } from '../../afe-catalog/structuring/well-workflow-status-badge';
import type {
  BaseStagedWell,
  SharedWellEditPanelProps,
  SharedWellListItem,
} from './well-review-match';
import styles from '../../afe-catalog/structuring/structuring-module.module.scss';

// ---------------------------------------------------------------------------
// Shared Well Overview section. ONE implementation for both the AFE and DO Well
// sections (they were self-described clones). Summary tiles + a Carbon DataTable
// (No. / Status / Extracted Well / Actions / Confirmed Match) with Add Well +
// inline Edit / Delete / Undo; every doctype divergence is injected via config:
//   • the wells atom bundle (overview data + error + reload + remove/restore) +
//     URL builders;
//   • the drill-in selection atom (AFE module-level singleton vs DO
//     document-scoped atomFamily) via `getSelectedWellIndexAtom`;
//   • the staged-meta field set via `stagedOverviewFields`;
//   • any extra post-mutation reload (DO also refreshes the Interest overview,
//     whose lines mute on a well's removal) via `useExtraReload`;
//   • an optional destructive-delete confirm modal via `confirmRemoval`
//     (DO confirms first; AFE deletes on the single icon click);
//   • the doctype Add / Edit panels.
// A failed load now surfaces an error + Retry (mirrors the Interest section)
// instead of falling through to an empty table.
// ---------------------------------------------------------------------------

/** GET .../wells — generic over the doctype's staged-well shape. */
export interface SharedWellOverviewResponse<S extends BaseStagedWell> {
  agentRunInProgress: boolean;
  summary: WellOverviewSummary;
  wells: SharedWellListItem<S>[];
}

type WellMutationAtom = SectionMutationAtom<WellLifecycleResponse>;

export interface SharedAddWellPanelProps {
  open: boolean;
  documentId: number | string;
  onClose: () => void;
  onAdded: () => void;
}

/** Doctype wiring for the shared Well Overview. */
export interface WellsOverviewConfig<S extends BaseStagedWell> {
  wellOverviewDataAtom: Atom<SharedWellOverviewResponse<S> | null>;
  /** Non-null after a failed fetch — drives the error + Retry state. */
  wellOverviewErrorAtom: Atom<string | null>;
  /** Reload the overview list (takes the overview href). */
  fetchWellOverviewAtom: WritableAtom<
    null,
    [string | undefined],
    Promise<void>
  >;
  removeWellAtom: WellMutationAtom;
  restoreWellAtom: WellMutationAtom;
  wellOverviewUrl: (documentId: number | string) => string;
  /** Canonical wells route by id — remove is this, restore is this + /restoration. */
  wellDetailUrl: (
    documentId: number | string,
    wellId: number | string
  ) => string;
  /** Drill-in selection setter atom (AFE singleton / DO document-scoped family). */
  getSelectedWellIndexAtom: (
    documentId: number | string
  ) => PrimitiveAtom<number | null>;
  /** Staged-meta cell fields (conditionally-shown fields are omitted; legal is fullWidth). */
  stagedOverviewFields: (
    staged: S
  ) => { label: string; value: string; fullWidth?: boolean }[];
  /**
   * Extra post-mutation reload hook. AFE is a no-op; DO refreshes the Interest
   * overview too (its lines mute/un-mute on a well remove/restore). Called
   * unconditionally so hook order stays stable.
   */
  useExtraReload: (documentId: number | string) => () => void;
  /** When set, Delete opens this confirm modal first (destructive-action norm). */
  confirmRemoval?: {
    heading: string;
    primaryButtonText: string;
    message: (wellName: string) => string;
  };
  AddWellPanel: FC<SharedAddWellPanelProps>;
  WellEditPanel: FC<SharedWellEditPanelProps<S>>;
}

export interface WellsOverviewSectionProps<S extends BaseStagedWell> {
  documentId: number | string;
  config: WellsOverviewConfig<S>;
}

const HEADERS = [
  { key: 'no', header: 'No.' },
  { key: 'status', header: 'Status' },
  { key: 'staged', header: 'Extracted Well from Document' },
  { key: 'actions', header: 'Actions' },
  { key: 'match', header: 'Confirmed Well Match' },
];

/**
 * Map the section summary status onto the shared StatusTag union.
 * @param {string} status - The well section summary status.
 * @return {PartyStatus} The matching StatusTag union value.
 */
const toPartyStatus = (status: WellOverviewSummary['status']): PartyStatus => {
  if (status === 'complete') return 'complete';
  if (status === 'removed') return 'removed';
  if (status === 'not_started') return 'not_started';
  return 'in_progress';
};

export function WellsOverviewSection<S extends BaseStagedWell>({
  documentId,
  config,
}: WellsOverviewSectionProps<S>) {
  type Well = SharedWellListItem<S>;
  const data = useAtomValue(config.wellOverviewDataAtom);
  const loadError = useAtomValue(config.wellOverviewErrorAtom);
  const reload = useSetAtom(config.fetchWellOverviewAtom);
  // Extra post-mutation reload (DO refreshes the Interest overview; AFE no-op).
  // Destructured so it reads as a stable hook call (config is module-constant per
  // doctype, so this is a fixed hook in a fixed position).
  const useExtraReload = config.useExtraReload;
  const reloadExtra = useExtraReload(documentId);
  const removeWell = useSetAtom(config.removeWellAtom);
  const restoreWell = useSetAtom(config.restoreWellAtom);
  const setSelectedWellIndex = useSetAtom(
    config.getSelectedWellIndexAtom(documentId)
  );
  const [addOpen, setAddOpen] = useState(false);
  // `editWell` holds the row under edit; `editOpen` drives the panel's open
  // state separately so closing toggles `open` → false (rather than unmounting
  // the panel). The slideIn SidePanel only resets the page-content margin it adds
  // on the open → false transition, so unmounting while still open would leave
  // the structuring shell with a leftover right-margin (whitespace).
  const [editWell, setEditWell] = useState<Well | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  // The well pending deletion (drives the optional confirm modal).
  const [deleteWell, setDeleteWell] = useState<Well | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const href = config.wellOverviewUrl(documentId);
  const agentRunInProgress = data?.agentRunInProgress ?? false;

  // Refresh the overview list plus any doctype-specific companion (DO interests).
  const reloadBoth = () => {
    reload(href);
    reloadExtra();
  };

  // Poll while the agent is running so the banner clears + suggestions appear.
  useEffect(() => {
    if (!agentRunInProgress) return undefined;
    const id = window.setInterval(() => reload(href), 3000);
    return () => window.clearInterval(id);
  }, [agentRunInProgress, href, reload]);

  const wells = data?.wells ?? [];
  const summary = data?.summary;
  const activeCount = wells.filter((w) => w.status !== 'removed').length;

  const rows = wells.map((w) => ({
    id: String(w.id),
    no: '',
    status: '',
    staged: w.stagedWell.wellName ?? '—',
    actions: '',
    match: w.confirmedMatch?.wellName ?? '',
  }));

  const wellById = (id: string): Well | undefined =>
    wells.find((w) => String(w.id) === id);

  const indexOfWell = (well: Well) => wells.indexOf(well);

  const handleRemove = (well: Well) => {
    const url = config.wellDetailUrl(documentId, well.id);
    setActionError(null);
    removeWell({
      url,
      method: 'DELETE',
      // Keep the confirm modal open until the outcome is known: close only on
      // success, so a failure stays visible in context (mirrors the Interest
      // delete modal) rather than dismissing before removeWell resolves.
      onSuccess: () => {
        setDeleteWell(null);
        reloadBoth();
      },
      onError: (m) =>
        setActionError(
          m ?? `Failed to remove ${well.stagedWell.wellName ?? 'well'}.`
        ),
    });
  };

  const handleRestore = (well: Well) => {
    const url = `${config.wellDetailUrl(documentId, well.id)}/restoration`;
    setActionError(null);
    restoreWell({
      url,
      method: 'POST',
      onSuccess: reloadBoth,
      onError: (m) =>
        setActionError(
          m ?? `Failed to restore ${well.stagedWell.wellName ?? 'well'}.`
        ),
    });
  };

  return (
    <div className={styles.overviewBody}>
      <div className={styles.overviewSectionTitleRow}>
        <h4 className={styles.overviewSectionTitle}>Well Information</h4>
        {summary && (
          <StatusTag
            status={toPartyStatus(summary.status)}
            progress={`${summary.counts.complete}/${activeCount}`}
          />
        )}
      </div>

      {summary && (
        <div className={styles.summaryTiles}>
          <Tile>
            <p className={styles.tileCount}>{summary.counts.notStarted}</p>
            <p className={styles.tileLabel}>Not Started</p>
          </Tile>
          <Tile>
            <p className={styles.tileCount}>{summary.counts.complete}</p>
            <p className={styles.tileLabel}>Complete</p>
          </Tile>
          <Tile>
            <p className={styles.tileCount}>{summary.counts.removed}</p>
            <p className={styles.tileLabel}>Removed</p>
          </Tile>
          <Tile>
            <p className={styles.tileCount}>{summary.counts.userEdited}</p>
            <p className={styles.tileLabel}>User Edited</p>
          </Tile>
          <Tile>
            <p className={styles.tileCount}>{summary.counts.userAdded}</p>
            <p className={styles.tileLabel}>User Added</p>
          </Tile>
        </div>
      )}

      {agentRunInProgress && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Well Matching Agent is running"
          subtitle="Matches are suggestions — confirm or reject each well before proceeding."
          style={{ maxWidth: '100%' }}
        >
          <InlineLoading description="" />
        </InlineNotification>
      )}

      {actionError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={actionError}
          onCloseButtonClick={() => setActionError(null)}
          style={{ maxWidth: '100%' }}
        />
      )}

      {loadError && !data ? (
        // A failed load must NOT fall through to an empty table — on
        // catalog-gating data, "looks empty" reading as "is empty" is the
        // dangerous failure. Show the error + a Retry instead (mirrors the
        // Interest section).
        <div>
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title="Unable to load wells"
            subtitle="Something went wrong loading this section."
            style={{ maxWidth: '100%' }}
          />
          <Button kind="tertiary" size="sm" onClick={() => reload(href)}>
            Retry
          </Button>
        </div>
      ) : (
        <DataTable rows={rows} headers={HEADERS}>
          {({
            rows: dtRows,
            headers,
            getHeaderProps,
            getTableProps,
            onInputChange,
          }: any) => (
            <TableContainer className={styles.wellsOverviewTable}>
              <TableToolbar size="sm">
                <TableToolbarContent>
                  <TableToolbarSearch
                    onChange={onInputChange}
                    placeholder="Search wells…"
                    persistent
                    size="sm"
                  />
                  <Button
                    kind="primary"
                    size="sm"
                    renderIcon={Add}
                    onClick={() => setAddOpen(true)}
                  >
                    Add Well
                  </Button>
                </TableToolbarContent>
              </TableToolbar>
              <Table {...getTableProps()} size="sm">
                <TableHead>
                  <TableRow>
                    {headers.map((h: any) => (
                      <TableHeader
                        {...getHeaderProps({ header: h })}
                        key={h.key}
                      >
                        {h.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dtRows.map((row: any) => {
                    const well = wellById(row.id);
                    if (!well) return null;
                    const isRemoved = well.status === 'removed';
                    const isReviewed = well.status === 'complete';
                    return (
                      <TableRow
                        key={row.id}
                        className={isRemoved ? styles.rowRemoved : undefined}
                        onClick={() => {
                          if (!isRemoved)
                            setSelectedWellIndex(indexOfWell(well));
                        }}
                        style={{ cursor: isRemoved ? 'default' : 'pointer' }}
                      >
                        {/* Well's position in the full list (not the filtered row
                            index) so "No." matches the drill-in "Well X of N" even
                            while a search filters the table. */}
                        <TableCell>{indexOfWell(well) + 1}</TableCell>
                        <TableCell>
                          <WellWorkflowStatusBadge
                            status={well.status}
                            userEdited={well.userEdited}
                            variant="inline"
                          />
                        </TableCell>
                        <TableCell>
                          <Link
                            href="#"
                            onClick={(e: MouseEvent) => e.preventDefault()}
                          >
                            {well.stagedWell.wellName ?? '—'}
                          </Link>
                          {/* Staged-meta fields owned by the doctype config. */}
                          <div className={styles.cellGrid}>
                            {config
                              .stagedOverviewFields(well.stagedWell)
                              .map((f) => (
                                <span
                                  key={f.label}
                                  className={
                                    f.fullWidth
                                      ? styles.cellGridFull
                                      : undefined
                                  }
                                >
                                  {f.label}:{' '}
                                  <span className={styles.cellMetaValue}>
                                    {f.value}
                                  </span>
                                </span>
                              ))}
                          </div>
                        </TableCell>
                        <TableCell
                          onClick={(e: MouseEvent) => e.stopPropagation()}
                        >
                          {isRemoved ? (
                            <Button
                              kind="ghost"
                              size="sm"
                              hasIconOnly
                              renderIcon={Undo}
                              iconDescription="Undo Remove"
                              tooltipPosition="top"
                              tooltipAlignment="end"
                              onClick={() => handleRestore(well)}
                            />
                          ) : (
                            <div className={styles.rowActions}>
                              <Button
                                kind="ghost"
                                size="sm"
                                hasIconOnly
                                renderIcon={Edit}
                                iconDescription={
                                  isReviewed
                                    ? 'To edit, un-match well first'
                                    : 'Edit'
                                }
                                tooltipPosition="top"
                                tooltipAlignment="end"
                                disabled={isReviewed}
                                onClick={() => {
                                  setEditWell(well);
                                  setEditOpen(true);
                                }}
                              />
                              <Button
                                kind="ghost"
                                size="sm"
                                hasIconOnly
                                renderIcon={TrashCan}
                                iconDescription={
                                  isReviewed
                                    ? 'To delete well, un-match first'
                                    : 'Delete Well'
                                }
                                tooltipPosition="top"
                                tooltipAlignment="end"
                                disabled={isReviewed}
                                onClick={() => {
                                  // Destructive: confirm first when the doctype
                                  // opts in; otherwise remove on the single click.
                                  if (config.confirmRemoval) {
                                    setActionError(null);
                                    setDeleteWell(well);
                                  } else handleRemove(well);
                                }}
                              />
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {well.confirmedMatch ? (
                            <>
                              <Link
                                href="#"
                                onClick={(e: MouseEvent) => e.preventDefault()}
                              >
                                {well.confirmedMatch.wellName ?? '—'}
                              </Link>
                              <div className={styles.cellMeta}>
                                {/* An imported-well match carries no UWI/API
                                    number (it has importedWellId/slotId instead),
                                    so the API row falls back to '—'. */}
                                <span>
                                  API Number:{' '}
                                  <span className={styles.cellMetaValue}>
                                    {well.confirmedMatch.uwi ?? '—'}
                                  </span>
                                </span>
                                <span>
                                  Hole Direction:{' '}
                                  <span className={styles.cellMetaValue}>
                                    {well.confirmedMatch.holeDirection ?? '—'}
                                  </span>
                                </span>
                                <span>
                                  Well Status:{' '}
                                  <span className={styles.cellMetaValue}>
                                    {well.confirmedMatch.wellStatus ?? '—'}
                                  </span>
                                </span>
                              </div>
                            </>
                          ) : well.pendingSuggestionsCount > 0 ? (
                            // Mirror the Parties match column: exact count, then
                            // no-match, then the not-yet-run fallback.
                            <span className={styles.cellMutedText}>
                              {well.pendingSuggestionsCount} suggestion
                              {well.pendingSuggestionsCount === 1
                                ? ''
                                : 's'}{' '}
                              available
                            </span>
                          ) : well.noMatch ? (
                            <span className={styles.cellMutedText}>
                              No matches found
                            </span>
                          ) : (
                            <span className={styles.cellMutedText}>
                              No suggestions available yet
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}

      <config.AddWellPanel
        open={addOpen}
        documentId={documentId}
        onClose={() => setAddOpen(false)}
        onAdded={() => {
          setAddOpen(false);
          reload(href);
        }}
      />

      {editWell && (
        <config.WellEditPanel
          open={editOpen}
          documentId={documentId}
          well={editWell}
          staged={editWell.stagedWell}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            reload(href);
          }}
        />
      )}

      {config.confirmRemoval && (
        // Destructive-delete confirmation. The DELETE fires only on the modal's
        // primary action.
        <Modal
          open={deleteWell !== null}
          danger
          size="sm"
          modalHeading={config.confirmRemoval.heading}
          primaryButtonText={config.confirmRemoval.primaryButtonText}
          secondaryButtonText="Cancel"
          onRequestClose={() => setDeleteWell(null)}
          onSecondarySubmit={() => setDeleteWell(null)}
          onRequestSubmit={() => {
            if (deleteWell) handleRemove(deleteWell);
          }}
        >
          <p>
            {config.confirmRemoval.message(
              deleteWell?.stagedWell.wellName ?? 'this well'
            )}
          </p>
          {actionError && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title="Remove failed"
              subtitle={actionError}
            />
          )}
        </Modal>
      )}
    </div>
  );
}
